import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/AppShell';
import AdminTabs from '../../components/AdminTabs';
import FormShell from '../../components/FormShell';
import ErrorBanner from '../../components/ErrorBanner';
import RichTextEditor from '../../components/RichTextEditor';
import PhotoUploader from '../../components/PhotoUploader';
import { supabase } from '../../lib/supabase';
import { sanitizeRichText } from '@viet-parenting/supabase/sanitize';
import { slugifyWithTimestamp } from '@viet-parenting/supabase/slug';
import type { BlogPost, Country, Status } from '@viet-parenting/supabase/types';
import { BlogPostDetail } from '@viet-parenting/content/detail';
import PreviewModal from '../../components/PreviewModal';
import PreviewButton from '../../components/PreviewButton';
import { draftIdentity } from '../../lib/preview';

interface FormState {
  title_vi: string;
  title_en: string;
  body_vi: string;
  body_en: string;
  cover_image: string | null;
  tags: string;
  country: string;
  status: Status;
  featured: boolean;
}

const EMPTY: FormState = {
  title_vi: '',
  title_en: '',
  body_vi: '',
  body_en: '',
  cover_image: null,
  tags: '',
  country: 'global',
  status: 'pending',
  featured: false,
};

/**
 * The database shape of the form. Used both for the insert/update payload and
 * for the preview, so an admin can never see something a save would not write.
 */
function toPayload(state: FormState) {
  return {
    title_vi: state.title_vi.trim(),
    title_en: state.title_en.trim() || null,
    body_vi: sanitizeRichText(state.body_vi) || null,
    body_en: state.body_en ? sanitizeRichText(state.body_en) : null,
    cover_image: state.cover_image,
    tags: state.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean),
    country: state.country || 'global',
    status: state.status,
    featured: state.featured,
    published_at: state.status === 'verified' ? new Date().toISOString() : null,
  };
}

function toPreviewRow(state: FormState, id: string | undefined): BlogPost {
  return {
    ...draftIdentity(state.title_vi),
    ...(id ? { id } : {}),
    ...toPayload(state),
  } as BlogPost;
}

interface Props {
  mode: 'new' | 'edit';
}

export default function AdminBlogPostForm({ mode }: Props) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.from('countries').select('*').order('sort');
      if (!active) return;
      setCountries((data ?? []) as Country[]);
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (mode === 'new' || !id) return;
    let active = true;
    (async () => {
      const { data, error: dbError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (!active) return;
      if (dbError || !data) {
        setError(dbError?.message ?? 'Không tìm thấy bài viết.');
        setLoading(false);
        return;
      }
      const p = data as BlogPost;
      setState({
        title_vi: p.title_vi,
        title_en: p.title_en ?? '',
        body_vi: p.body_vi ?? '',
        body_en: p.body_en ?? '',
        cover_image: p.cover_image,
        tags: p.tags.join(', '),
        country: p.country,
        status: p.status,
        featured: p.featured ?? false,
      });
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [mode, id]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setState((s) => ({ ...s, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!state.title_vi.trim()) {
      setError('Tiêu đề là bắt buộc.');
      return;
    }
    setSaving(true);
    setError(null);

    const payload = toPayload(state);

    const result =
      mode === 'new'
        ? await supabase
            .from('blog_posts')
            .insert({ ...payload, slug: slugifyWithTimestamp(state.title_vi) })
        : await supabase.from('blog_posts').update(payload).eq('id', id!);

    setSaving(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    navigate('/admin/blog');
  };

  const handleDelete = async () => {
    if (mode !== 'edit' || !id) return;
    const ok = window.confirm(
      `Xóa bài viết "${state.title_vi}"? Hành động này không thể hoàn tác.`,
    );
    if (!ok) return;
    setDeleting(true);
    setError(null);
    const { error: dbError } = await supabase.from('blog_posts').delete().eq('id', id);
    setDeleting(false);
    if (dbError) {
      setError(dbError.message);
      return;
    }
    navigate('/admin/blog');
  };

  const title = mode === 'new' ? 'Tạo bài viết mới' : 'Chỉnh sửa bài viết';

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <AdminTabs active="blog" />
          <FormShell title={title} maxWidth="max-w-6xl">
            <p className="text-sm text-gray-400">Đang tải…</p>
          </FormShell>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <AdminTabs active="blog" />
        <FormShell title={title} maxWidth="max-w-6xl">
          <ErrorBanner message={error} />

          <form onSubmit={handleSubmit}>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="space-y-8">
            <section className="brand-card p-6 space-y-5">
              <h2 className="font-semibold">Bài viết</h2>
              <Field label="Tiêu đề (tiếng Việt)" required>
                <input
                  type="text"
                  required
                  value={state.title_vi}
                  onChange={(e) => update('title_vi', e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Tiêu đề (tiếng Anh)">
                <input
                  type="text"
                  value={state.title_en}
                  onChange={(e) => update('title_en', e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Nội dung (tiếng Việt)">
                <RichTextEditor
                  value={state.body_vi}
                  onChange={(html) => update('body_vi', html)}
                  placeholder="Nội dung bài viết..."
                />
              </Field>
              <Field label="Nội dung (tiếng Anh)">
                <RichTextEditor
                  value={state.body_en}
                  onChange={(html) => update('body_en', html)}
                  placeholder="Body in English..."
                />
              </Field>
            </section>
              </div>

              <div className="space-y-8">
            <section className="brand-card p-6 space-y-5">
              <h2 className="font-semibold">Ảnh xem trước</h2>
              <p className="text-sm text-gray-500 -mt-3">
                Hiển thị trên thẻ bài viết ở trang danh sách và ở đầu bài viết.
              </p>
              <PhotoUploader
                value={state.cover_image ? [state.cover_image] : []}
                onChange={(ids) => update('cover_image', ids[0] ?? null)}
                maxFiles={1}
              />
            </section>

            <section className="brand-card p-6 space-y-5">
              <h2 className="font-semibold">Phân loại</h2>
              <Field label="Tags (cách nhau bằng dấu phẩy)">
                <input
                  type="text"
                  value={state.tags}
                  onChange={(e) => update('tags', e.target.value)}
                  placeholder="văn hóa, song ngữ"
                  className={inputClass}
                />
              </Field>
              <Field label="Quốc gia" required>
                <select
                  required
                  value={state.country}
                  onChange={(e) => update('country', e.target.value)}
                  className={inputClass}
                >
                  <option value="global">🌐 Toàn cầu (Global)</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.name_vi} ({c.code})
                    </option>
                  ))}
                </select>
              </Field>
            </section>
              </div>
            </div>

            <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-8 px-4 sm:px-6 py-4 bg-white/75 backdrop-blur-md border-t border-gray-200 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate('/admin/blog')}
                className="border border-gray-200 text-gray-700 text-sm font-medium px-5 py-2.5 rounded-lg hover:border-red-300 transition-colors"
              >
                Hủy
              </button>
              <PreviewButton onClick={() => setPreviewing(true)} disabled={!state.title_vi.trim()} />
              {mode === 'edit' && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting || saving}
                  className="border border-red-200 text-red-700 text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-red-50 hover:border-red-300 disabled:opacity-50 transition-colors"
                >
                  {deleting ? 'Đang xóa…' : 'Xóa'}
                </button>
              )}
              <div className="flex items-center gap-4 ml-auto text-sm flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={state.featured}
                    onChange={(e) => update('featured', e.target.checked)}
                  />
                  <span className="font-medium text-gray-700">★ Featured</span>
                </label>
                <span className="font-medium text-gray-700">Trạng thái:</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="pending"
                    checked={state.status === 'pending'}
                    onChange={() => update('status', 'pending')}
                  />
                  Pending
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="verified"
                    checked={state.status === 'verified'}
                    onChange={() => update('status', 'verified')}
                  />
                  Active
                </label>
              </div>
              <button
                type="submit"
                disabled={saving}
                className="bg-red-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-red-800 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Đang lưu…' : mode === 'new' ? 'Tạo bài viết' : 'Lưu thay đổi'}
              </button>
            </div>
          </form>
        </FormShell>
      </div>

      {previewing && (
        <PreviewModal onClose={() => setPreviewing(false)}>
          {(ctx) => <BlogPostDetail post={toPreviewRow(state, id)} ctx={ctx} />}
        </PreviewModal>
      )}
    </AppShell>
  );
}

const inputClass =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500';

interface FieldProps {
  label: string;
  required?: boolean;
  children: ReactNode;
}
function Field({ label, required, children }: FieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
    </div>
  );
}
